// src/components/waypoint/CallScreen.tsx

import { Mic, MicOff, PhoneOff, Volume2, VolumeX } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { WaypointSessionDetail } from "@/hooks/use-waypoint-sessions";

// ── Types ─────────────────────────────────────────────────────────────────────

type ConnectionStatus = "connecting" | "connected" | "disconnected" | "error";
type PersonaStatus = "idle" | "thinking" | "speaking";

type OutboundMsg =
  | { type: "audio_end" }
  | { type: "end" }
  | { type: "set_tts_enabled"; enabled: boolean };

type InboundMsg =
  | { type: "transcript_partial"; text: string }
  | { type: "transcript_final"; text: string; speaker: string }
  | { type: "persona_thinking"; personaSlug: string }
  | { type: "audio_end"; personaSlug: string }
  | { type: "session_ended" }
  | { type: "trust_update" }
  | { type: "coaching_nudge" }
  | { type: "debrief_ready" }
  | { type: "error"; message: string };

type TranscriptEntry = {
  id: number;
  speaker: "user" | string;
  text: string;
};

// ── Audio helpers ─────────────────────────────────────────────────────────────

const SAMPLE_RATE = 16_000;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

const AVATAR_COLORS = ["#2D9BD6", "#407EC1", "#9B5DE5", "#2EA86A", "#E8A93C"];

// ── Hook ──────────────────────────────────────────────────────────────────────

function useCallSession(sessionId: string, micStream: MediaStream | undefined) {
  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const nextPlayTimeRef = useRef(0);
  const entryCounterRef = useRef(0);

  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [personaStatus, setPersonaStatus] = useState<PersonaStatus>("idle");
  const [activePersona, setActivePersona] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [partialText, setPartialText] = useState("");
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false);
  const [isTtsEnabled, setIsTtsEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem("waypoint_tts_enabled") !== "false";
    } catch {
      return true;
    }
  });
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Keep the mute ref in sync with state so audio processor can read it
  // without being a dependency of the WebSocket effect.
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Session timer
  useEffect(() => {
    if (status !== "connected") return;
    const id = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const send = useCallback((msg: OutboundMsg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const scheduleAudio = useCallback((samples: Float32Array) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const buf = ctx.createBuffer(1, samples.length, SAMPLE_RATE);
    buf.getChannelData(0).set(samples);
    const node = ctx.createBufferSource();
    node.buffer = buf;
    node.connect(ctx.destination);
    const now = ctx.currentTime;
    // If playback has fallen behind (first chunk of a segment, or a gap), give a 60 ms
    // head start to avoid scheduling in the past, which causes an audible glitch.
    const startAt = nextPlayTimeRef.current > now ? nextPlayTimeRef.current : now + 0.06;
    node.start(startAt);
    nextPlayTimeRef.current = startAt + buf.duration;
  }, []);

  const startMic = useCallback(async () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    // Use the stream obtained during the user gesture in BriefScreen; fall back
    // to getUserMedia if it wasn't passed (direct navigation to this route).
    const stream =
      micStream ?? (await navigator.mediaDevices.getUserMedia({ audio: true, video: false }));
    const srcNode = ctx.createMediaStreamSource(stream);
    sourceRef.current = srcNode;
    // ScriptProcessorNode requires a power-of-2 buffer size; 2048 ≈ 128ms at 16 kHz.
    const proc = ctx.createScriptProcessor(2048, 1, 1);
    proc.onaudioprocess = (e) => {
      if (isMutedRef.current) return;
      if (wsRef.current?.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      const int16 = new Int16Array(input.length);
      for (let i = 0; i < input.length; i++) {
        int16[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32768)));
      }
      wsRef.current.send(int16.buffer);
    };
    srcNode.connect(proc);
    // Route processor through a muted gain node so onaudioprocess fires without echoing mic audio to speakers
    const silencer = ctx.createGain();
    silencer.gain.value = 0;
    proc.connect(silencer);
    silencer.connect(ctx.destination);
    processorRef.current = proc;
  }, [micStream]);

  const hangUp = useCallback(() => {
    send({ type: "end" });
    wsRef.current?.close();
  }, [send]);

  const toggleTts = useCallback(() => {
    setIsTtsEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("waypoint_tts_enabled", String(next));
      } catch {
        // ignore storage errors
      }
      send({ type: "set_tts_enabled", enabled: next });
      return next;
    });
  }, [send]);

  useEffect(() => {
    const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
    audioCtxRef.current = ctx;
    let cancelled = false;

    async function connect() {
      let wsUrl = `/api/ws/session/${sessionId}`;
      try {
        const res = await fetch("/api/waypoint/ws-token", { method: "POST" });
        if (res.ok) {
          const { token } = (await res.json()) as { token: string };
          wsUrl = `/api/ws/session/${sessionId}?token=${token}`;
        }
      } catch {
        // Fall back to cookie auth
      }

      if (cancelled) return;

      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus("connected");
        // Sync TTS preference with server immediately on connect
        let ttsOn = true;
        try {
          ttsOn = localStorage.getItem("waypoint_tts_enabled") !== "false";
        } catch {
          // ignore
        }
        ws.send(JSON.stringify({ type: "set_tts_enabled", enabled: ttsOn }));
        ctx
          .resume()
          .then(() => startMic())
          .catch(() => {
            // mic unavailable — user will see the muted state, call continues
          });
      };
      ws.onclose = () => {
        if (!cancelled) setStatus("disconnected");
      };
      ws.onerror = () => {
        if (!cancelled) setStatus("error");
      };

      ws.onmessage = (evt) => {
        // Binary frames carry raw PCM audio from the persona (Int16, 16 kHz)
        if (evt.data instanceof ArrayBuffer) {
          const pcm = new Int16Array(evt.data);
          const samples = new Float32Array(pcm.length);
          for (let i = 0; i < pcm.length; i++) {
            samples[i] = (pcm[i] ?? 0) / 32768;
          }
          scheduleAudio(samples);
          setPersonaStatus("speaking");
          return;
        }
        // Text frames are JSON control messages
        const msg: InboundMsg = JSON.parse(evt.data as string);
        switch (msg.type) {
          case "transcript_partial":
            setPartialText(msg.text);
            break;
          case "transcript_final":
            setPartialText("");
            // speaker "sa" = sales agent (user); any other slug = persona
            if (msg.speaker === "sa") {
              setTranscript((prev) => [
                ...prev,
                { id: (entryCounterRef.current += 1), speaker: "user", text: msg.text },
              ]);
            } else {
              setTranscript((prev) => [
                ...prev,
                { id: (entryCounterRef.current += 1), speaker: msg.speaker, text: msg.text },
              ]);
            }
            break;
          case "persona_thinking":
            setActivePersona(msg.personaSlug);
            setPersonaStatus("thinking");
            break;
          case "audio_end":
            setPersonaStatus("idle");
            setActivePersona(null);
            break;
          case "session_ended":
          case "debrief_ready":
            setStatus("disconnected");
            break;
          case "error":
            setStatus("error");
            break;
          // trust_update and coaching_nudge are informational — no UI action needed yet
        }
      };
    }

    connect().catch(() => {
      if (!cancelled) setStatus("error");
    });

    return () => {
      cancelled = true;
      wsRef.current?.close();
      processorRef.current?.disconnect();
      sourceRef.current?.disconnect();
      ctx.close();
    };
  }, [sessionId, startMic, scheduleAudio]);

  return {
    status,
    personaStatus,
    activePersona,
    transcript,
    partialText,
    isMuted,
    setIsMuted,
    isTtsEnabled,
    toggleTts,
    hangUp,
    elapsedSeconds,
  };
}

// ── SpeakingBars ─────────────────────────────────────────────────────────────

function SpeakingBars({ color }: { color: string }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 18 }}>
      {[0, 0.18, 0.36].map((delay) => (
        <span
          key={delay}
          style={{
            width: 3,
            height: "100%",
            background: color,
            borderRadius: 999,
            transformOrigin: "bottom",
            animation: `wp-bars 0.7s infinite ${delay}s`,
          }}
        />
      ))}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

type Props = {
  session: WaypointSessionDetail;
  onEnded: () => void;
  notes?: string;
  micStream?: MediaStream;
};

export function CallScreen({ session, onEnded, notes, micStream }: Props) {
  const {
    status,
    personaStatus,
    activePersona,
    transcript,
    partialText,
    isMuted,
    setIsMuted,
    isTtsEnabled,
    toggleTts,
    hangUp,
    elapsedSeconds,
  } = useCallSession(session.id, micStream);

  const transcriptEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (transcript.length >= 0 || partialText !== undefined) {
      transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcript, partialText]);

  useEffect(() => {
    if (status === "disconnected" || status === "error") {
      onEnded();
    }
  }, [status, onEnded]);

  const scen = session.scenario;
  const personaMap = scen?.personaMap ?? {};

  // Build video tiles for all personas in the room
  const tiles = (scen?.roomRoles ?? []).map((role, i) => {
    const p = personaMap[role.personaSlug];
    const isActive = activePersona === role.personaSlug;
    const color = AVATAR_COLORS[i % AVATAR_COLORS.length] ?? "#2D9BD6";
    return {
      personaSlug: role.personaSlug,
      name: p?.name ?? role.personaSlug,
      title: p?.title ?? "",
      avBg: color,
      initials: p ? initials(p.name) : "?",
      active: isActive,
      color,
      border: isActive
        ? personaStatus === "speaking"
          ? "#2EA86A"
          : "#E8A93C"
        : "rgba(127,194,236,0.12)",
      glow:
        isActive && personaStatus === "speaking"
          ? "0 0 0 1.5px #2EA86A, 0 8px 30px rgba(46,168,106,0.25)"
          : "none",
    };
  });

  // Win condition (first one)
  const winCondition = scen?.winConditions[0] ?? "Complete the stakeholder conversation.";

  return (
    <div
      style={{
        height: "100vh",
        background: "#05070F",
        display: "grid",
        gridTemplateColumns: "1fr 372px",
        overflow: "hidden",
      }}
    >
      {/* ── Left: call area ────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "18px 20px 0",
          minWidth: 0,
        }}
      >
        {/* Goal banner */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "linear-gradient(90deg, rgba(45,155,214,0.16), rgba(100,71,157,0.10))",
            border: "1px solid rgba(127,194,236,0.28)",
            borderRadius: 12,
            padding: "12px 18px",
            marginBottom: 16,
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="#7FC2EC"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 18, height: 18, flexShrink: 0 }}
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
          </svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: "uppercase" as const,
                letterSpacing: "0.12em",
                color: "#7FC2EC",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              Goal
            </span>
            <span style={{ fontSize: 13.5, color: "#E6EEF8", marginLeft: 10 }}>{winCondition}</span>
          </div>
          <span
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              background:
                status === "connected" ? "rgba(46,168,106,0.15)" : "rgba(127,194,236,0.1)",
              color: status === "connected" ? "#2EA86A" : "#7FC2EC",
              fontFamily: "'Montserrat', sans-serif",
              border: `1px solid ${status === "connected" ? "rgba(46,168,106,0.3)" : "rgba(127,194,236,0.2)"}`,
            }}
          >
            {status === "connecting" ? "Connecting…" : status === "connected" ? "Live" : "Ended"}
          </span>
        </div>

        {/* Video grid */}
        <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: 12,
              height: "100%",
            }}
          >
            {tiles.map((tile) => (
              <div
                key={tile.personaSlug}
                style={{
                  position: "relative",
                  borderRadius: 16,
                  background: "linear-gradient(160deg, #0C1426, #070B16)",
                  border: `2px solid ${tile.border}`,
                  boxShadow: tile.glow,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "border-color 0.3s, box-shadow 0.3s",
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    width: 92,
                    height: 92,
                    borderRadius: 999,
                    background: tile.avBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                    fontSize: 30,
                    color: "#fff",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
                  }}
                >
                  {tile.initials}
                </div>

                {/* Speaking bars */}
                {tile.active && (
                  <div style={{ position: "absolute", top: 14, right: 14 }}>
                    <SpeakingBars color={tile.color} />
                  </div>
                )}

                {/* Name plate */}
                <div style={{ position: "absolute", left: 14, bottom: 12, right: 14 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#fff",
                      textShadow: "0 1px 4px rgba(0,0,0,0.6)",
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    {tile.name}
                  </div>
                  <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.65)" }}>
                    {tile.title}
                  </div>
                </div>
              </div>
            ))}

            {/* Placeholder tiles if fewer than 4 personas */}
            {tiles.length < 4 &&
              ["slot-a", "slot-b", "slot-c"].slice(0, 4 - tiles.length).map((slotKey) => (
                <div
                  key={slotKey}
                  style={{
                    borderRadius: 16,
                    background: "rgba(12,20,38,0.4)",
                    border: "1px dashed rgba(127,194,236,0.08)",
                  }}
                />
              ))}
          </div>

          {/* Self PiP */}
          <div
            style={{
              position: "absolute",
              left: 16,
              bottom: 16,
              width: 134,
              height: 84,
              borderRadius: 12,
              background: "linear-gradient(160deg, #11203F, #0A1428)",
              border: `2px solid ${!isMuted ? "#2EA86A" : "rgba(127,194,236,0.2)"}`,
              boxShadow: !isMuted ? "0 0 0 1.5px #2EA86A, 0 8px 30px rgba(46,168,106,0.2)" : "none",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 999,
                background: "linear-gradient(135deg, #2D9BD6, #5461AD)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                color: "#fff",
              }}
            >
              You
            </div>
            <div
              style={{
                position: "absolute",
                left: 9,
                bottom: 7,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  background: isMuted ? "#D8443C" : "#2EA86A",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isMuted ? (
                  <MicOff style={{ width: 10, height: 10, color: "#fff" }} />
                ) : (
                  <Mic style={{ width: 10, height: 10, color: "#fff" }} />
                )}
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>You</span>
            </div>
          </div>

          {/* Session timer */}
          <div
            style={{
              position: "absolute",
              right: 18,
              top: 14,
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(5,7,15,0.7)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.1)",
              padding: "7px 13px",
              borderRadius: 999,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: status === "connected" ? "#D8443C" : "#64748b",
                display: "inline-block",
                animation: status === "connected" ? "wp-pulse 1.4s infinite" : "none",
              }}
            />
            <span
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: 13.5,
                color: "#fff",
                fontVariantNumeric: "tabular-nums" as const,
              }}
            >
              {formatTime(elapsedSeconds)}
            </span>
          </div>
        </div>

        {/* Controls bar */}
        <div style={{ padding: "16px 4px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {/* Mic button */}
            <button
              type="button"
              onClick={() => setIsMuted((m) => !m)}
              style={{
                width: 56,
                height: 56,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                background: isMuted
                  ? "linear-gradient(135deg, #D8443C, #B03030)"
                  : "linear-gradient(135deg, #1E6CC7, #2D9BD6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isMuted
                  ? "0 6px 20px rgba(216,68,60,0.35)"
                  : "0 6px 20px rgba(30,108,199,0.35)",
                flexShrink: 0,
              }}
            >
              {isMuted ? (
                <MicOff style={{ width: 22, height: 22, color: "#fff" }} />
              ) : (
                <Mic style={{ width: 22, height: 22, color: "#fff" }} />
              )}
            </button>

            {/* Voice (TTS) toggle */}
            <button
              type="button"
              onClick={toggleTts}
              title={isTtsEnabled ? "Turn off AI voice" : "Turn on AI voice"}
              style={{
                width: 56,
                height: 56,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                background: isTtsEnabled
                  ? "linear-gradient(135deg, #1E6CC7, #2D9BD6)"
                  : "rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isTtsEnabled ? "0 6px 20px rgba(30,108,199,0.35)" : "none",
                flexShrink: 0,
              }}
            >
              {isTtsEnabled ? (
                <Volume2 style={{ width: 22, height: 22, color: "#fff" }} />
              ) : (
                <VolumeX style={{ width: 22, height: 22, color: "rgba(255,255,255,0.5)" }} />
              )}
            </button>

            {/* Hang up */}
            <button
              type="button"
              onClick={hangUp}
              disabled={status !== "connected"}
              style={{
                width: 56,
                height: 56,
                borderRadius: 999,
                border: "none",
                cursor: status === "connected" ? "pointer" : "not-allowed",
                background:
                  status === "connected"
                    ? "linear-gradient(135deg, #D8443C, #B03030)"
                    : "rgba(255,255,255,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: status === "connected" ? "0 6px 20px rgba(216,68,60,0.35)" : "none",
                flexShrink: 0,
              }}
            >
              <PhoneOff style={{ width: 22, height: 22, color: "#fff" }} />
            </button>

            {/* Status text */}
            <div style={{ flex: 1 }}>
              {partialText && (
                <p
                  style={{
                    fontSize: 13,
                    color: "rgba(255,255,255,0.6)",
                    margin: 0,
                    fontStyle: "italic",
                  }}
                >
                  {partialText}
                </p>
              )}
              {!partialText && activePersona && personaStatus !== "idle" && (
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", margin: 0 }}>
                  {personaMap[activePersona]?.name ?? activePersona}{" "}
                  {personaStatus === "thinking" ? "is thinking…" : "is speaking"}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: transcript panel ──────────────────────────────────────────── */}
      <div
        style={{
          background: "#070B16",
          borderLeft: "1px solid rgba(127,194,236,0.08)",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Panel header */}
        <div
          style={{
            padding: "18px 20px 14px",
            borderBottom: "1px solid rgba(127,194,236,0.08)",
            flexShrink: 0,
          }}
        >
          <p
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: 12,
              textTransform: "uppercase" as const,
              letterSpacing: "0.1em",
              color: "#7FC2EC",
              margin: 0,
            }}
          >
            Live transcript
          </p>
        </div>

        {/* Pinned notes */}
        {notes && notes.trim().length > 0 && (
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid rgba(127,194,236,0.08)",
              background: "rgba(127,194,236,0.04)",
              flexShrink: 0,
            }}
          >
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: 9,
                textTransform: "uppercase" as const,
                letterSpacing: "0.14em",
                color: "#7FC2EC",
                margin: "0 0 6px",
              }}
            >
              Your notes
            </p>
            <p
              style={{
                fontSize: 12,
                color: "rgba(230,238,248,0.55)",
                lineHeight: 1.6,
                margin: 0,
                whiteSpace: "pre-wrap" as const,
              }}
            >
              {notes}
            </p>
          </div>
        )}

        {/* Transcript entries */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {transcript.length === 0 && !partialText && (
            <p
              style={{
                fontSize: 13,
                color: "rgba(255,255,255,0.2)",
                margin: "20px 0 0",
                textAlign: "center",
              }}
            >
              Transcript will appear here
            </p>
          )}

          {transcript.map((entry) => {
            const isUser = entry.speaker === "user";
            const name = isUser ? "You" : (personaMap[entry.speaker]?.name ?? entry.speaker);
            const roleIdx = (scen?.roomRoles ?? []).findIndex(
              (r) => r.personaSlug === entry.speaker,
            );
            const color = isUser
              ? "#2D9BD6"
              : (AVATAR_COLORS[roleIdx >= 0 ? roleIdx : 0] ?? "#2D9BD6");

            return (
              <div key={entry.id} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color,
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  {name}
                </span>
                <p
                  style={{
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.82)",
                    margin: 0,
                  }}
                >
                  {entry.text}
                </p>
              </div>
            );
          })}

          {partialText && (
            <div style={{ display: "flex", flexDirection: "column", gap: 3, opacity: 0.6 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#2D9BD6",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                You
              </span>
              <p
                style={{
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.7)",
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                {partialText}
              </p>
            </div>
          )}

          <div ref={transcriptEndRef} />
        </div>

        {/* Scenario name at bottom */}
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid rgba(127,194,236,0.08)",
            flexShrink: 0,
          }}
        >
          <p
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.3)",
              margin: 0,
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              textTransform: "uppercase" as const,
              letterSpacing: "0.1em",
            }}
          >
            {scen?.name ?? "Session"}
          </p>
        </div>
      </div>
    </div>
  );
}
